import React, { FC, ReactElement, useState } from 'react'
import { Alert } from 'antd'
import { Provider } from 'react-redux'
import { Requests, apiCall } from '../../api'
import { Snapdocs } from '../../hooks/api'
import { store } from '../../store'
import { StackedDocument, StackedDocumentsProps } from './types'
import PreviewRemoteDocument from '../PreviewRemoteDocument/PreviewRemoteDocument'

// TODO: QC-3282 migrate to Ant, when attachments table is migrated to React
export const StackedDocuments: FC<StackedDocumentsProps> = ({ orderId, role = 'admin' }): ReactElement | null => {
  const { response: qcReport } = Snapdocs.useResources(Requests.titleQCReport({ orderId }))
  const { combinedDocuments } = qcReport || {}
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewName, setPreviewName] = useState<string | null>(null)

  const formatMetadata = (document: StackedDocument): string => {
    if (!document.fileSize && !document.pageSizes) {
      return ''
    }

    const parts: string[] = []

    if (document.fileSize) {
      const sizeInMB = Math.round(document.fileSize / (1024 * 1024))
      parts.push(`${sizeInMB} MB`)
    }

    if (document.pageSizes) {
      const pageBreakdown: string[] = []

      if (document.pageSizes.letter && document.pageSizes.letter.length > 0) {
        pageBreakdown.push(`Letter: ${document.pageSizes.letter.length} pg`)
      }
      if (document.pageSizes.legal && document.pageSizes.legal.length > 0) {
        pageBreakdown.push(`Legal: ${document.pageSizes.legal.length} pg`)
      }
      if (document.pageSizes.unknown && document.pageSizes.unknown.length > 0) {
        pageBreakdown.push(`Other: ${document.pageSizes.unknown.length} pg`)
      }

      if (pageBreakdown.length > 0) {
        parts.push(pageBreakdown.join(' '))
      }
    }

    return parts.join(' | ')
  }

  const handleDocumentPreview = async (remoteDocumentId: string, documentName: string) => {
    try {
      const request = Requests.titleQCReportDownloadDocument({ orderId, remoteDocumentId })
      const response = await apiCall.get(request.path, { params: request.params })
      setPreviewName(documentName)
      setPreviewUrl(response.data.download_url)
    } catch (error) {
      console.error('Failed to fetch document preview URL:', error)
    }
  }

  const handleDocumentDownload = async (remoteDocumentId: string, documentName: string) => {
    try {
      const request = Requests.titleQCReportDownloadDocument({ orderId, remoteDocumentId })
      const response = await apiCall.get(request.path, { params: request.params })

      const link = document.createElement('a')
      link.href = response.data.download_url
      link.download = documentName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Failed to download document:', error)
    }
  }

  const handleClosePreview = () => {
    setPreviewUrl(null)
    setPreviewName(null)
  }

  if (!combinedDocuments || combinedDocuments.length === 0) {
    return null
  }

  const isAdmin = role === 'admin'

  return (
    <>
      <div className="combined-docs-subheader">Stacked</div>
      <table className={`table attachments--regular ${role}-attachments`} style={{ borderBottom: '1px solid #ddd' }}>
        <tbody className="files">
          {combinedDocuments.map((document: StackedDocument) => (
            <React.Fragment key={document.remoteDocumentId}>
              <tr className="template-download">
                <td className={`${isAdmin ? 'col-md-5' : 'col-md-7'} col-xs-9 attachment-name-block`}>
                  <span className="name">
                    <button
                      className="btn-link file-name"
                      type="button"
                      style={{ padding: 0, border: 0 }}
                      onClick={() => handleDocumentPreview(document.remoteDocumentId, document.filename)}
                    >
                      {document.filename}
                    </button>
                    {document.addedByWithTimestamp && (
                      <>
                        <br />
                        <small className="text-muted">{document.addedByWithTimestamp}</small>
                      </>
                    )}
                  </span>
                </td>
                {isAdmin && <td className="col-md-3 hidden-xs center">{/* Empty status column */}</td>}
                <td className={`${isAdmin ? 'col-md-2' : 'col-md-3'} hidden-xs`}>
                  <span className="text-muted">{formatMetadata(document)}</span>
                </td>
                <td className="col-md-2 col-xs-3 text-right helper-buttons-block">
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={() => handleDocumentDownload(document.remoteDocumentId, document.filename)}
                  >
                    <i className="fa fa-download"></i>
                  </button>
                </td>
              </tr>
              {document.compressionTargetMissed && (
                <tr>
                  <td colSpan={isAdmin ? 4 : 3} style={{ borderTop: 0 }}>
                    <Alert
                      type="error"
                      showIcon
                      message={`Set to compress to ${document.maxFileSizeMb} MB, but unable to compress further.`}
                    />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <div className="combined-docs-subheader" style={{ marginTop: 15 }}>
        Originals & Scanbacks
      </div>
      <PreviewRemoteDocument
        open={!!previewUrl}
        documentName={previewName || ''}
        pdfUrl={previewUrl || ''}
        onClose={handleClosePreview}
      />
    </>
  )
}

const WithProvider: FC<StackedDocumentsProps> = (props): ReactElement => (
  <Provider store={store}>
    <StackedDocuments {...props} />
  </Provider>
)

export default WithProvider
